import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Loader2, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { studyStaff, ApiError, STUDY_STAFF_ROLES, isBlindedStudyType, type StudyStaffRole, type StudyBlindingType } from '@/api/client';
import { humanRole } from '@/lib/humanise';
import { useAuth } from '@/contexts/AuthContext';
import { canManageStudyStaff } from '@/lib/staffPermissions';
import { errorText } from '@/lib/permissionError';
import { REVIEWER_SITE_ROLES } from '@/lib/siteRoles';

/**
 * Study team roles. Monitors and auditors are not offered (13 Oct 2026): they
 * reach a study only through Monitor Assignments, which is time-boxed and
 * records their blinding. STUDY_STAFF_ROLES itself stays whole — Training
 * targets reviewers too.
 */
const ASSIGNABLE_ROLES = STUDY_STAFF_ROLES.filter((r) => !REVIEWER_SITE_ROLES.includes(r));

export interface AssignStaffDialogProps {
  studyId: string;
  /** Read-only display label — e.g. "STD-2026-0001 — Online RCT of Brief CBT". */
  studyLabel: string;
  /** Punch-list DT2-BLIND-3 — the blinded/unblinded toggle only appears
   *  for blinded studies; Open-Label (or unset) hides it. */
  blindingType?: StudyBlindingType | null;
  open: boolean;
  /** Before activation any onboarder picks the team (Harish, 2 Oct 2026). Defaults to activated. */
  activated?: boolean;
  onClose: () => void;
  onAssigned: () => void;
}

/**
 * Replaces the old "Manage staff" → full onboarding-wizard redirect with a
 * focused modal: study is fixed (read-only), pick a site user not yet on
 * the study, pick a role, done. Reuses the same /studies/{id}/staff
 * endpoints the onboarding wizard's Staff card already calls, so this
 * creates a real delegation — same training-trigger wiring, same DoA
 * eligibility — just without forcing a detour through the wizard.
 */
export function AssignStaffDialog({
  studyId,
  studyLabel,
  blindingType,
  open,
  activated = true,
  onClose,
  onAssigned,
}: AssignStaffDialogProps) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<StudyStaffRole | ''>('');
  // Mandatory on blinded studies: nobody is assigned without an explicit
  // Blinded / Unblinded designation (client, 2026-09-23). Hidden on
  // open-label studies where there is nothing to choose.
  const [designation, setDesignation] = useState<'blinded' | 'unblinded' | ''>('');
  // Two-click remove: the first click arms the row, the second confirms.
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const showBlindingToggle = isBlindedStudyType(blindingType);

  const staffQ = useQuery({
    queryKey: ['study-staff', studyId],
    queryFn: () => studyStaff.list(studyId),
    enabled: open,
    staleTime: 30_000,
  });
  const candidates = staffQ.data?.candidates ?? [];
  const assignments = staffQ.data?.assignments ?? [];

  const canAssign = canManageStudyStaff(user?.role, user?.id, assignments, activated);

  useEffect(() => {
    if (open) {
      setUserId('');
      setRole('');
      setDesignation('');
      setConfirmRemoveId(null);
    }
  }, [open, studyId]);

  // Removing staff after activation had no screen at all — the only Remove
  // button lived in the onboarding wizard (RCA 7 Oct 2026).
  const removeMut = useMutation({
    mutationFn: (delegationId: string) => studyStaff.revoke(studyId, delegationId),
    onSuccess: () => {
      toast.success('Staff member removed from the study');
      setConfirmRemoveId(null);
      qc.invalidateQueries({ queryKey: ['study-staff', studyId] });
      qc.invalidateQueries({ queryKey: ['workspace', studyId] });
      qc.invalidateQueries({ queryKey: ['study', studyId, 'delegated-users'] });
      onAssigned();
    },
    onError: (err) => {
      setConfirmRemoveId(null);
      toast.error(errorText(err, 'Failed to remove staff member'));
    },
  });

  const assignMut = useMutation({
    mutationFn: () => {
      if (!userId || !role) throw new Error('Pick a user and a role.');
      if (showBlindingToggle && !designation) {
        throw new Error('Choose whether this person is Blinded or Unblinded on this study.');
      }
      return studyStaff.assign(studyId, {
        user_id: userId,
        role,
        blinded: showBlindingToggle ? designation === 'blinded' : undefined,
      });
    },
    onSuccess: (assignment) => {
      toast.success(`${assignment.full_name} assigned as ${humanRole(assignment.role)}`);
      qc.invalidateQueries({ queryKey: ['study-staff', studyId] });
      qc.invalidateQueries({ queryKey: ['workspace', studyId] });
      qc.invalidateQueries({ queryKey: ['study', studyId, 'delegated-users'] });
      onAssigned();
      onClose();
    },
    onError: (err) => {
      toast.error(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to assign staff member',
      );
    },
  });

  if (!open) return null;

  const isValid = !!userId && !!role && (!showBlindingToggle || !!designation);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4 !m-0"
      onClick={(e) => {
        if (e.target === e.currentTarget && !assignMut.isPending && !removeMut.isPending) onClose();
      }}
    >
      <div className="card w-full max-w-md space-y-4 p-6">
        <div className="flex items-start justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-ink-primary">
            <UserPlus className="h-4 w-4" />
            Manage study staff
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={assignMut.isPending || removeMut.isPending}
            className="text-ink-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!staffQ.isLoading && !canAssign && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            Only the study&apos;s PI, Sub-Investigator or Coordinator, a Site Admin or a Site Study
            Designer can manage its staff, and you don&apos;t hold one of those roles
            ({humanRole(user?.role ?? '')} sign-in).
          </div>
        )}

        <div>
          <label className="label text-xs">Study</label>
          <div className="input bg-surface-muted/50 text-ink-muted">{studyLabel}</div>
        </div>

        {assignments.length > 0 && (
          <div>
            <div className="label text-xs">Current staff</div>
            <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-surface-border p-1.5">
              {assignments.map((a) => (
                <li key={a.delegation_id} className="flex items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-surface-muted/40">
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium text-ink-primary">{a.full_name || a.email}</span>{' '}
                    <span className="text-ink-muted">· {humanRole(a.role)}</span>
                    {a.active === false && <span className="ml-1 text-[10px] text-ink-muted">(inactive)</span>}
                  </span>
                  {canAssign && (
                    <button
                      type="button"
                      className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium disabled:opacity-40 ${
                        confirmRemoveId === a.delegation_id
                          ? 'bg-danger-600 text-white hover:bg-danger-700'
                          : 'text-ink-muted hover:text-danger-700'
                      }`}
                      disabled={removeMut.isPending || assignMut.isPending}
                      aria-label={`Remove ${a.full_name} from the study`}
                      onClick={() =>
                        confirmRemoveId === a.delegation_id
                          ? removeMut.mutate(a.delegation_id)
                          : setConfirmRemoveId(a.delegation_id)
                      }
                    >
                      {removeMut.isPending && removeMut.variables === a.delegation_id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : confirmRemoveId === a.delegation_id ? (
                        'Confirm remove'
                      ) : (
                        <X className="h-3.5 w-3.5" />
                      )}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <label className="label text-xs" htmlFor="assign-staff-user">
            User *
          </label>
          {staffQ.isLoading ? (
            <div className="input flex items-center gap-2 text-xs text-ink-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading site users…
            </div>
          ) : staffQ.isError ? (
            <div className="text-xs text-danger-700">Failed to load site staff.</div>
          ) : candidates.length === 0 ? (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Every active site user is already assigned to this study.
            </div>
          ) : (
            <div className="relative">
              <select
                id="assign-staff-user"
                className="input w-full appearance-none pr-8"
                value={userId}
                disabled={assignMut.isPending || !canAssign}
                onChange={(e) => setUserId(e.target.value)}
              >
                <option value="" disabled>
                  Pick a site user…
                </option>
                {candidates.map((c) => (
                  <option key={c.user_id} value={c.user_id}>
                    {c.full_name} ({c.email})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-ink-muted" />
            </div>
          )}
        </div>

        <div>
          <label className="label text-xs" htmlFor="assign-staff-role">
            Role *
          </label>
          <div className="relative">
            <select
              id="assign-staff-role"
              className="input w-full appearance-none pr-8"
              value={role}
              disabled={assignMut.isPending || !canAssign}
              onChange={(e) => setRole(e.target.value as StudyStaffRole)}
            >
              <option value="" disabled>
                Pick a role…
              </option>
              {ASSIGNABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {humanRole(r)}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-ink-muted" />
          </div>
        </div>

        {showBlindingToggle && (
          <fieldset
            className={`rounded-md border px-3 py-2 text-xs ${designation ? 'border-surface-border bg-surface-muted/30' : 'border-amber-300 bg-amber-50'
              }`}
            data-testid="assign-staff-blinding"
          >
            <legend className="label text-xs px-1">
              Blinding designation <span className="text-danger-600">*</span>
            </legend>
            <div className="mt-1 grid gap-1.5 sm:grid-cols-2">
              <label className="flex cursor-pointer items-start gap-2 rounded border border-transparent px-2 py-1.5 hover:bg-white/60">
                <input
                  type="radio"
                  name="assign-staff-designation"
                  value="blinded"
                  checked={designation === 'blinded'}
                  disabled={assignMut.isPending || !canAssign}
                  onChange={() => setDesignation('blinded')}
                  className="mt-0.5"
                />
                <span>
                  <b className="text-ink-primary">Blinded</b>
                  <span className="block text-[11px] text-ink-muted">
                    Patient-facing — sees subjects and eSource; no IP / randomisation documents.
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 rounded border border-transparent px-2 py-1.5 hover:bg-white/60">
                <input
                  type="radio"
                  name="assign-staff-designation"
                  value="unblinded"
                  checked={designation === 'unblinded'}
                  disabled={assignMut.isPending || !canAssign}
                  onChange={() => setDesignation('unblinded')}
                  className="mt-0.5"
                />
                <span>
                  <b className="text-ink-primary">Unblinded</b>
                  <span className="block text-[11px] text-ink-muted">
                    Allocation-aware — sees the unblinded (IP) binder; no subject identity or eSource.
                  </span>
                </span>
              </label>
            </div>
            {!designation && (
              <p className="mt-1 text-[11px] text-amber-800">Required on a blinded study.</p>
            )}
          </fieldset>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={assignMut.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!isValid || !canAssign || assignMut.isPending}
            onClick={() => assignMut.mutate()}
          >
            {assignMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="h-4 w-4" />
            )}
            Assign staff member
          </button>
        </div>
      </div>
    </div>
  );
}
